import { create } from 'zustand';

const KEY = 'research_chat_open';

/**
 * Open/closed state for the global "Ask a legal question" research chat pane
 * (desktop right dock / mobile bottom sheet). The preference persists in
 * localStorage, but "open" is only restored on desktop widths — auto-reopening
 * a near-full-screen sheet on a phone reload would be disorienting (the mobile
 * sheet is an explicit, momentary surface; the desktop dock is a workspace
 * preference — Windows 11 Copilot / Chrome Gemini pattern).
 */
const initialOpen = (): boolean => {
  try {
    return (
      localStorage.getItem(KEY) === '1' &&
      window.matchMedia('(min-width: 1024px)').matches
    );
  } catch {
    return false;
  }
};

const persist = (open: boolean) => {
  try {
    localStorage.setItem(KEY, open ? '1' : '0');
  } catch {
    /* private mode — non-fatal */
  }
};

interface ChatPaneState {
  isOpen: boolean;
  open: () => void;
  close: () => void;
  toggle: () => void;
}

export const useChatPaneStore = create<ChatPaneState>((set, get) => ({
  isOpen: initialOpen(),
  open: () => {
    persist(true);
    set({ isOpen: true });
  },
  close: () => {
    persist(false);
    set({ isOpen: false });
  },
  toggle: () => (get().isOpen ? get().close() : get().open()),
}));
